import { DependencyList, useEffect, useState } from 'react';
import { navigateToUrl } from 'single-spa';
import { useAuthUser, useAuthSession, useAuthUserClaims } from '@nbx/frontend-helpers/subscribe';
import { NBXStorage } from '@nbx/frontend-helpers/storage';

const microapps = window.MICROFRONTENDS_CONFIG;

const log = (...args: unknown[]): void => console.log('useRedirectOnboardingFlow:', ...args);

const storageKey = role => `onboardingRedirected${role}`;
const isAlreadyRedirected = role => NBXStorage.getItem(storageKey(role)).then(value => value === '1');
const writeRedirected = role => NBXStorage.setItem(storageKey(role), 1);

export interface UseRedirectOnboardingFlow {
  enabled?: boolean;
  isCardPartnershipOnboarding?: boolean;
}

export const useRedirectOnboardingFlow = (
  { enabled = true, isCardPartnershipOnboarding = false }: UseRedirectOnboardingFlow = {
    enabled: true,
    isCardPartnershipOnboarding: false
  },
  deps?: DependencyList
) => {
  const user = useAuthUser();
  const session = useAuthSession();
  const claims = useAuthUserClaims(user?.id);
  const [redirected, setRedirected] = useState<null | boolean>(null);

  useEffect(() => {
    const redirect = async () => {
      if (!claims?.roles || !user || !session) {
        return;
      }

      const { roles } = claims;
      const [redirectedKYC, redirectedTOTP, redirectedCARD] = await Promise.all([
        isAlreadyRedirected('KYC'),
        isAlreadyRedirected('TOTP'),
        isAlreadyRedirected('CARD')
      ]);

      if (
        !roles.includes('KYC') &&
        !roles.includes('KYC-ACCEPTED') &&
        (isCardPartnershipOnboarding || !redirectedKYC)
      ) {
        log(`redirect to ${microapps?.routes.verification.path}`);
        navigateToUrl(microapps?.routes.verification.path);
        await writeRedirected('KYC');
        setRedirected(true);
      } else if (
        (roles.includes('KYC') || roles.includes('KYC-ACCEPTED')) &&
        !roles.includes('TOTP') &&
        (isCardPartnershipOnboarding || !redirectedTOTP)
      ) {
        log(`redirect to ${microapps?.routes.accountSecurity.path}?showTwoFactorModal=1`);
        navigateToUrl(`${microapps?.routes.accountSecurity.path}?showTwoFactorModal=1`);
        await writeRedirected('TOTP');
        setRedirected(true);
      } else if (
        roles.includes('KYC') &&
        roles.includes('TOTP') &&
        isCardPartnershipOnboarding &&
        !redirectedCARD
      ) {
        log(
          `redirect to ${microapps?.routes.card.path} isCardPartnershipOnboarding=${isCardPartnershipOnboarding}`
        );
        navigateToUrl(`${microapps?.routes.card.path}`);
        await writeRedirected('CARD');
        setRedirected(true);
      } else {
        setRedirected(false);
      }
    };

    if (enabled) redirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, claims, user, session, ...(deps || [])]);

  return { redirected };
};
